<template>
  <v-dialog persistent v-model="dialog" content-class="bg-white font-lato p-4" max-width="800"  >
    <div class="d-flex align-items-center justify-content-between overflow-auto mb-6" style="row-gap: 0.8rem">
      <h4 class="mb-0">Upload File</h4>
      <v-btn @click="closeModal()" icon>
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </div>
    <div class="d-flex flex-column">
      <Dropzone @files-dropped="addFiles" @files-selected="addFiles" :disabled="files.length >= 10"/>
      <FileList :files="files" :removeFile="removeFile" :getProgressColor="getProgressColor" />
      <CasesSelector :addToCase="addToCase" :casesLoading="casesLoading" :cases="caseItems" :selectedCase="selectedCase"
        :allCasesLoaded="allCasesLoaded" @search-query-change="onSearchQueryChange" @load-more="loadMoreCases"
        @toggle-selection="toggleSelection" @select-case="handleSelectCase" />
    </div>
    <div class="buttons d-flex justify-content-end mt-4">
      <div class="btn btn-outline-primary mr-2" @click="closeModal()">
        Cancel
      </div>
      <button type="submit" class="btn btn-primary" :disabled="isSaveDisabled || isUploading" @click="saveFiles">
        <span v-if="isUploading" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
        <span>{{ isUploading ? 'Uploading ...' : 'Upload' }}</span>
      </button>
    </div>
  </v-dialog>
</template>

<script>
import { mapActions } from 'vuex';
import Dropzone from '../upload-modal/dropzone.vue';
import FileList from '../upload-modal/file-list.vue';
import CasesSelector from '../upload-modal/cases-selector.vue';
import { debounce } from 'lodash';
import { ALLOWED_FILE_TYPES } from '../../constants/constants';

export default {
  components: {
    Dropzone,
    FileList,
    CasesSelector,
  },
  props: ['dialog'],
  data() {
    return {
      files: [],
      uploadedFiles: [],
      hovered: false,
      directUploadsUrl: "/rails/active_storage/direct_uploads",
      addToCase: false,
      casesLoading: false,
      filteredCases: [],
      selectedCase: null,
      allCasesLoaded: false,
      isUploading: false,
    }
  },
  computed: {
    isSaveDisabled() {
      return this.files.length === 0 || this.files.some(file => file.uploading);
    },
    caseItems() {
      return this.$store.state.cases;
    },
    casesTotal() {
      return this.$store.state.casesTotal;
    },
    casesPage() {
      return this.$store.state.casesPage;
    },
    casesTotalPages() {
      return this.$store.state.casesTotalPages;
    },
    hasMoreCases() {
      return this.casesPage < this.casesTotalPages;
    },
  },
  methods: {
    ...mapActions(['deleteFile']),
    closeModal() {
      const uploadingFiles = this.files.some(file => file.uploading);
      if (uploadingFiles) {
        return;
      }

      this.removeAllFiles();
      this.addToCase = false;
      this.$emit('toggle-modal', false);
    },
    saveFiles() {
      this.isUploading = true;

      let data = {
        supporting_documents: this.files.map(file => ({
          name: file.name,
          file: file.blobSignedId
        }))
      }

      if (this.selectedCase) {
        data.case_id = this.selectedCase
      }

      this.$store.dispatch('uploadFile', data)
        .then(() => {
          this.files = []
          this.selectedCase = null
          this.addToCase = false
          this.$emit('toggle-modal', false)
        })
        .finally(() => {
          this.isUploading = false;
        });
    },
    removeFile(file) {
      if (file.uploading) {
        return;
      }
      
      this.deleteFile(file.blobSignedId)
        .then(() => {
          this.files = this.files.filter(f => f !== file);
        });
    },
    removeAllFiles() {
      const filesToRemove = this.files.filter(file => !file.uploading && file.blobSignedId);
      
      if (filesToRemove.length === 0) {
        this.files = [];
        return;
      }
      
      const deletePromises = this.files
        .filter(file => file.blobSignedId)
        .map(file => this.deleteFile(file.blobSignedId));

      Promise.all(deletePromises)
        .then(() => {
          this.files = [];
        });
    },
    getProgressColor(progress) {
      return progress === 100 ? '#3DA568' : '#007BFF';
    },
    addFiles(files) {
      if (this.files.length + files.length > 10) {
        alert('You can upload a maximum of 10 files.');
        return;
      }

      for (const file of files) {
        if (!ALLOWED_FILE_TYPES.includes(file.type)) {
          alert(`${file.name} is not a supported format.`);
          return;
        }        
      }

      Array.from(files).forEach(file => {
        const fileObj = {
          file,
          name: file.name,
          size: file.size,
          progress: 0,
          uploading: true,
          blobSignedId: null,
        };
        this.files.push(fileObj);
        this.uploadFile(fileObj);
      });
    },
    uploadFile(fileObj) {
      const upload = new ActiveStorage.DirectUpload(fileObj.file, this.directUploadsUrl, {
        directUploadWillStoreFileWithXHR: (request) => {
          request.upload.addEventListener("progress", (event) => {
            if (event.lengthComputable) {
              fileObj.progress = (event.loaded / event.total) * 100;
            }
          });
        }
      });

      upload.create((error, blob) => {
        if (error) {
          alert('Error uploading file. Please try again.', error);
          this.files = this.files.filter(file => file !== fileObj);
          console.error(error);
        } else {
          fileObj.blobSignedId = blob.signed_id;

          setTimeout(() => {
            fileObj.uploading = false;
          }, 3000);
        }
      });
    },
    toggleSelection(isChecked) {
      this.addToCase = isChecked;
      if (isChecked) {
        this.getAllCases();
      } else {
        this.selectedCase = null;
      }
    },
    loadMoreCases() {
      if (this.hasMoreCases) {
        this.$store.commit('setCasesPage', this.$store.state.casesPage + 1);
        this.getAllCases();
      }
    },
    onSearchQueryChange(query) {
      this.debouncedHandleSearchQueryChange(query);
    },
    handleSearchQueryChange(query) {
      this.$store.commit('setCasesQuery', query);
      this.$store.commit('setCasesPage', 1);
      this.allCasesLoaded = false;
      this.getAllCases();
    },
    handleSelectCase(selectedItem) {
      this.selectedCase = selectedItem;
    },
    getAllCases() {
      if (this.addToCase) {
        this.casesLoading = true;
        this.$store.dispatch('getUploadCases', { page: this.casesPage })
          .then(() => {
            this.allCasesLoaded = !this.hasMoreCases;
          })
          .catch(error => {
            console.error('Error fetching cases:', error);
          })
          .finally(() => {
            this.casesLoading = false;
          });
      }
    }
  },
  created() {
    this.debouncedHandleSearchQueryChange = debounce(this.handleSearchQueryChange, 500);
  }
}
</script>